import { useState, useEffect } from 'react';

const API_URL = 'https://bot.devit.es/api';
const headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('devit_token')}`
});

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tenants');

  const [blogTema, setBlogTema] = useState('');
  const [blogPalabrasClave, setBlogPalabrasClave] = useState('');
  const [blogGenerando, setBlogGenerando] = useState(false);
  const [borradores, setBorradores] = useState([]);
  const [publicados, setPublicados] = useState([]);
  const [vistaPrevia, setVistaPrevia] = useState(null);

  const [scheduleTema, setScheduleTema] = useState('');
  const [schedulePlataforma, setSchedulePlataforma] = useState('linkedin');
  const [scheduleTono, setScheduleTono] = useState('profesional');
  const [scheduleFecha, setScheduleFecha] = useState('');
  const [scheduleHora, setScheduleHora] = useState('');
  const [scheduleImagen, setScheduleImagen] = useState('');
  const [programaciones, setProgramaciones] = useState([]);
  const [editandoLimite, setEditandoLimite] = useState({});
  const [driveConectado, setDriveConectado] = useState(false);

  const load = () => {
    Promise.all([
      fetch(`${API_URL}/admin/stats`, { headers: headers() }).then(r => r.json()),
      fetch(`${API_URL}/admin/tenants`, { headers: headers() }).then(r => r.json()),
    ]).then(([s, t]) => {
      setStats(s);
      setTenants(Array.isArray(t) ? t : []);
      setLoading(false);
    });
  };

  const checkDrive = async () => {
    try {
      const token = localStorage.getItem('devit_token');
      const res = await fetch(`${API_URL}/drive/status?authorization=Bearer%20${token}`);
      const data = await res.json();
      setDriveConectado(data.connected);
    } catch {
      setDriveConectado(false);
    }
  };

  const cargarBorradores = async () => {
    const res = await fetch(`${API_URL}/admin/blog/borradores`, { headers: headers() });
    const data = await res.json();
    setBorradores(data);
  };

  const cargarPublicados = async () => {
    const res = await fetch(`${API_URL}/admin/blog/publicados`, { headers: headers() });
    const data = await res.json();
    setPublicados(data);
  };

  const cargarProgramaciones = async () => {
    const res = await fetch(`${API_URL}/admin/schedule`, { headers: headers() });
    const data = await res.json();
    setProgramaciones(data);
  };

  const toggle = async (id) => {
    await fetch(`${API_URL}/admin/tenants/${id}/toggle`, { method: 'PUT', headers: headers() });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este tenant?')) return;
    await fetch(`${API_URL}/admin/tenants/${id}`, { method: 'DELETE', headers: headers() });
    load();
  };

  // ── NUEVO: toggle módulo social ──────────────────────────────────────────
  const toggleSocial = async (id) => {
    await fetch(`${API_URL}/admin/tenants/${id}/social`, { method: 'PUT', headers: headers() });
    load();
  };

  const guardarLimite = async (id) => {
    const limite = editandoLimite[id];
    if (!limite || isNaN(limite)) return alert('Introduce un número válido');
    const res = await fetch(`${API_URL}/admin/tenants/${id}/limite`, {
      method: 'PUT',
      headers: headers(),
      body: JSON.stringify({ tokens_limite: parseInt(limite) })
    });
    if (res.ok) {
      setEditandoLimite(prev => ({ ...prev, [id]: undefined }));
      load();
    }
  };

  const crearProgramacion = async () => {
    if (!scheduleTema.trim()) return alert('Escribe un tema');
    if (!scheduleFecha || !scheduleHora) return alert('Elige fecha y hora');

    if (schedulePlataforma === 'instagram' && !scheduleImagen.trim()) {
      return alert('Instagram requiere una imagen. Escribe el nombre del archivo en tu carpeta "Devit Media" de Google Drive.');
    }

    const fechaHora = new Date(`${scheduleFecha}T${scheduleHora}`).toISOString();
    const body = {
      tema: scheduleTema,
      plataforma: schedulePlataforma,
      tono: scheduleTono,
      fecha_hora: fechaHora,
    };

    if (scheduleImagen.trim()) {
      body.imagen_nombre = scheduleImagen.trim();
    }

    const res = await fetch(`${API_URL}/admin/schedule`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(body)
    });
    if (res.ok) {
      alert('Publicación programada');
      setScheduleTema('');
      setScheduleImagen('');
      cargarProgramaciones();
    } else {
      const err = await res.json();
      alert('Error: ' + (err.detail || 'desconocido'));
    }
  };

  const eliminarProgramacion = async (id) => {
    if (window.confirm('¿Eliminar esta programación?')) {
      await fetch(`${API_URL}/admin/schedule/${id}`, { method: 'DELETE', headers: headers() });
      cargarProgramaciones();
    }
  };

  const generarBorrador = async () => {
    if (!blogTema.trim()) return alert('Escribe un tema');
    setBlogGenerando(true);
    try {
      const res = await fetch(`${API_URL}/admin/blog/generar`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ tema: blogTema, palabras_clave: blogPalabrasClave, incluir_faqs: true })
      });
      const data = await res.json();
      if (data.exito) {
        setVistaPrevia(data);
        cargarBorradores();
        alert('Borrador generado correctamente');
      } else {
        alert('Error: ' + (data.detail || 'desconocido'));
      }
    } catch {
      alert('Error de conexión');
    }
    setBlogGenerando(false);
  };

  const publicarBorrador = async (postId) => {
    const res = await fetch(`${API_URL}/admin/blog/publicar`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ post_id: postId })
    });
    if (res.ok) {
      alert('Artículo publicado');
      cargarBorradores();
      setVistaPrevia(null);
    } else {
      alert('Error al publicar');
    }
  };

  useEffect(() => { load(); checkDrive(); }, []);

  useEffect(() => {
    if (tab === 'blog') {
      setVistaPrevia(null);
      cargarBorradores();
      cargarPublicados();
      cargarProgramaciones();
    }
  }, [tab]);

  return (
    <div>
      <div className="topbar">
        <div>
          <div className="page-title">Panel Admin</div>
          <div className="page-sub">Gestión de clientes y blog</div>
        </div>
      </div>

      <div className="tab-row" style={{ marginBottom: '20px' }}>
        <div className={`tab ${tab === 'tenants' ? 'active' : ''}`} onClick={() => setTab('tenants')}>Clientes</div>
        <div className={`tab ${tab === 'blog' ? 'active' : ''}`} onClick={() => setTab('blog')}>Blog SEO</div>
      </div>

      {tab === 'tenants' && (
        <>
          <div className="metrics" style={{marginBottom:'20px'}}>
            <div className="metric metric-green">
              <div className="metric-label">Total clientes</div>
              <div className="metric-value">{stats?.total_tenants || 0}</div>
            </div>
            <div className="metric metric-cyan">
              <div className="metric-label">Activos</div>
              <div className="metric-value">{stats?.activos || 0}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Con Stripe</div>
              <div className="metric-value">{stats?.con_stripe || 0}</div>
            </div>
            <div className="metric metric-amber">
              <div className="metric-label">Tokens mes</div>
              <div className="metric-value">{Math.round((stats?.tokens_mes || 0)/1000)}k</div>
            </div>
          </div>

          <div className="card" style={{marginBottom:'16px'}}>
            <div className="card-title">Distribución por plan</div>
            <div style={{display:'flex', gap:'8px', flexWrap:'wrap', marginTop:'4px'}}>
              {stats?.por_plan?.map(p => (
                <span key={p.plan} className="badge badge-cyan">{p.plan}: {p.total}</span>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Clientes</div>
            <table className="tenants-table">
              <thead>
                <tr>
                  <th>Empresa</th><th>Email</th><th>Plan</th><th>Especialidad</th>
                  <th>WhatsApp</th><th>Calendar</th><th>Estado</th>
                  <th>Social</th>{/* ── NUEVO */}
                  <th>Acciones</th><th>Límite tokens</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id}>
                    <td style={{color:'var(--text)'}}>{t.nombre}</td>
                    <td>{t.email}</td>
                    <td><span className="badge badge-cyan">{t.plan}</span></td>
                    <td>{t.especialidad || '—'}</td>
                    <td>{t.whatsapp ? <span className="badge badge-green">✓</span> : <span className="badge badge-red">✗</span>}</td>
                    <td>{t.google_calendar ? <span className="badge badge-green">✓</span> : <span className="badge badge-red">✗</span>}</td>
                    <td><span className={`badge ${t.activo ? 'badge-green' : 'badge-red'}`}>{t.activo ? 'activo' : 'inactivo'}</span></td>
                    {/* ── NUEVO: columna módulo social ── */}
                    <td>
                      <button
                        onClick={() => toggleSocial(t.id)}
                        title={t.modulo_social ? 'Desactivar módulo social' : 'Activar módulo social'}
                        style={{
                          background:   t.modulo_social ? '#1a3a2a' : '#2a2e4a',
                          border:       `1px solid ${t.modulo_social ? '#2a5a3a' : '#3a3e5a'}`,
                          color:        t.modulo_social ? '#4ade80' : '#5a5e82',
                          borderRadius: 6,
                          padding:      '3px 10px',
                          cursor:       'pointer',
                          fontSize:     11,
                          fontWeight:   600,
                        }}
                      >
                        {t.modulo_social ? '📣 ON' : '📣 OFF'}
                      </button>
                    </td>
                    <td>
                      <div style={{display:'flex', gap:'6px'}}>
                        <button className="btn btn-ghost" style={{fontSize:'10px', padding:'3px 8px'}} onClick={() => toggle(t.id)}>
                          {t.activo ? 'Pausar' : 'Activar'}
                        </button>
                        <button className="btn btn-danger" style={{fontSize:'10px', padding:'3px 8px'}} onClick={() => remove(t.id)}>
                          Borrar
                        </button>
                      </div>
                    </td>
                    <td>
                      <div style={{display:'flex', gap:'4px', alignItems:'center'}}>
                        <input type="number"
                          style={{width:'90px', fontSize:'11px', padding:'3px 6px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:'4px', color:'var(--text)'}}
                          value={editandoLimite[t.id] !== undefined ? editandoLimite[t.id] : (t.tokens_limite || 5000000)}
                          onChange={e => setEditandoLimite(prev => ({...prev, [t.id]: e.target.value}))}
                        />
                        {editandoLimite[t.id] !== undefined && (
                          <button className="btn btn-ghost" style={{fontSize:'10px', padding:'3px 6px'}} onClick={() => guardarLimite(t.id)}>✓</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'blog' && (
        <div className="content">

          <div className="card" style={{marginBottom:'16px', padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                <span style={{fontSize:'20px'}}>📁</span>
                <div>
                  <div style={{fontSize:'13px', color:'var(--text)', fontWeight:600}}>Google Drive — Devit Media</div>
                  <div style={{fontSize:'11px', color:'var(--text3)', marginTop:'2px'}}>
                    {driveConectado
                      ? 'Conectado — sube tus imágenes a la carpeta "Devit Media" en tu Drive'
                      : 'No conectado — necesario para publicar con imágenes en redes sociales'}
                  </div>
                </div>
              </div>
              {driveConectado ? (
                <button className="btn btn-danger" style={{fontSize:'11px', padding:'5px 12px'}}
                  onClick={async () => {
                    const token = localStorage.getItem('devit_token');
                    await fetch(`${API_URL}/drive/disconnect?authorization=Bearer%20${token}`, { method: 'DELETE' });
                    setDriveConectado(false);
                  }}>
                  Desconectar
                </button>
              ) : (
                <button className="btn btn-ghost" style={{fontSize:'11px', padding:'5px 12px'}}
                  onClick={async () => {
                    const token = localStorage.getItem('devit_token');
                    const res = await fetch(`${API_URL}/drive/connect?authorization=Bearer%20${token}`);
                    const data = await res.json();
                    if (data.auth_url) window.location.href = data.auth_url;
                  }}>
                  Conectar Drive
                </button>
              )}
            </div>
          </div>

          <div className="card" style={{marginBottom:'20px'}}>
            <div className="card-title">Generar nuevo artículo (SEO/GEO)</div>
            <div className="form-group">
              <label className="form-label">Tema principal</label>
              <input className="form-input" placeholder="Ej: Cómo los agentes IA mejoran la productividad en pymes"
                value={blogTema} onChange={e => setBlogTema(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Palabras clave (opcional, separadas por comas)</label>
              <input className="form-input" placeholder="ia, automatización, productividad"
                value={blogPalabrasClave} onChange={e => setBlogPalabrasClave(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={generarBorrador} disabled={blogGenerando}>
              {blogGenerando ? 'Generando...' : 'Generar borrador'}
            </button>
          </div>

          <div className="card" style={{marginTop:'20px'}}>
            <div className="card-title">Artículos publicados</div>
            {publicados.length === 0 && <div>No hay artículos publicados.</div>}
            {publicados.map(p => (
              <div key={p.id} style={{marginBottom:'12px', borderBottom:'1px solid var(--border)', padding:'8px 0'}}>
                <div><strong>{p.titulo}</strong> (publicado: {new Date(p.publicado_en).toLocaleString()})</div>
                <div style={{marginTop:'6px'}}>
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{fontSize:'12px', marginRight:'8px'}}>Ver</a>
                  <button className="btn btn-danger" style={{fontSize:'12px'}} onClick={async () => {
                    if (window.confirm('¿Eliminar este artículo permanentemente?')) {
                      await fetch(`${API_URL}/admin/blog/posts/${p.id}`, { method: 'DELETE', headers: headers() });
                      cargarPublicados();
                      cargarBorradores();
                    }
                  }}>Eliminar</button>
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{marginTop:'20px'}}>
            <div className="card-title">Programar nueva publicación</div>
            <div className="form-group">
              <label className="form-label">Tema</label>
              <input className="form-input" placeholder="Ej: Beneficios de la IA en RRHH"
                value={scheduleTema} onChange={e => setScheduleTema(e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plataforma</label>
                <select className="form-input" value={schedulePlataforma} onChange={e => { setSchedulePlataforma(e.target.value); setScheduleImagen(''); }}>
                  <option value="linkedin">LinkedIn</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tono</label>
                <select className="form-input" value={scheduleTono} onChange={e => setScheduleTono(e.target.value)}>
                  <option value="profesional">Profesional</option>
                  <option value="inspirador">Inspirador</option>
                  <option value="educativo">Educativo</option>
                  <option value="provocador">Provocador</option>
                  <option value="casual">Casual</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Imagen desde Google Drive
                {schedulePlataforma === 'instagram' && (
                  <span style={{color:'var(--amber)', marginLeft:'6px', fontSize:'10px'}}>⚠ Obligatorio para Instagram</span>
                )}
                {schedulePlataforma !== 'instagram' && (
                  <span style={{color:'var(--text3)', marginLeft:'6px', fontSize:'10px'}}>Opcional para LinkedIn y Facebook</span>
                )}
              </label>
              <input
                className="form-input"
                placeholder='Nombre del archivo en carpeta "Devit Media" — ej: foto_producto.jpg'
                value={scheduleImagen}
                onChange={e => setScheduleImagen(e.target.value)}
                style={{borderColor: schedulePlataforma === 'instagram' && !scheduleImagen ? 'var(--amber)' : undefined}}
              />
              {!driveConectado && (
                <div style={{fontSize:'11px', color:'var(--amber)', marginTop:'4px'}}>
                  ⚠ Conecta Google Drive para publicar con imágenes
                </div>
              )}
              {driveConectado && (
                <div style={{fontSize:'11px', color:'var(--text3)', marginTop:'4px'}}>
                  📁 El archivo debe estar en la carpeta "Devit Media" de tu Google Drive
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Fecha</label>
                <input type="date" className="form-input" value={scheduleFecha} onChange={e => setScheduleFecha(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Hora (hora de Madrid)</label>
                <input type="time" step="60" className="form-input" value={scheduleHora} onChange={e => setScheduleHora(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" onClick={crearProgramacion}>Programar publicación</button>
          </div>

          <div className="card" style={{marginTop:'20px'}}>
            <div className="card-title">Publicaciones programadas</div>
            {programaciones.length === 0 && <div>No hay publicaciones programadas.</div>}
            {programaciones.map(p => (
              <div key={p.id} style={{marginBottom:'12px', borderBottom:'1px solid var(--border)', padding:'8px 0'}}>
                <div><strong>{p.tema}</strong> — {p.plataforma}</div>
                {p.imagen_nombre && (
                  <div style={{fontSize:'11px', color:'var(--text3)', marginTop:'2px'}}>
                    📸 Imagen: {p.imagen_nombre}
                  </div>
                )}
                <div>Programada para: {(() => {
                  if (!p.fecha_hora) return 'Fecha no disponible';
                  const fecha = typeof p.fecha_hora === 'number' ? new Date(p.fecha_hora) : new Date(p.fecha_hora);
                  return isNaN(fecha) ? 'Fecha inválida' : fecha.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', hour12: false });
                })()}</div>
                <div>Estado: {p.publicado ? '✅ Publicada' : '⏳ Pendiente'}</div>
                {!p.publicado && (
                  <button className="btn btn-danger" style={{fontSize:'12px', marginTop:'6px'}} onClick={() => eliminarProgramacion(p.id)}>
                    Cancelar
                  </button>
                )}
              </div>
            ))}
          </div>

          {vistaPrevia && vistaPrevia.contenido_html && (
            <div className="card" style={{marginBottom:'20px'}}>
              <div className="card-title">Vista previa del borrador</div>
              <div><strong>Título:</strong> {vistaPrevia.titulo}</div>
              <div><strong>Meta descripción:</strong> {vistaPrevia.meta_descripcion}</div>
              <div><strong>Slug:</strong> {vistaPrevia.slug}</div>
              <div dangerouslySetInnerHTML={{ __html: vistaPrevia.contenido_html }} />
              {vistaPrevia.faqs && (
                <div>
                  <strong>FAQs:</strong>
                  {vistaPrevia.faqs.map((faq, i) => (
                    <div key={i}><b>{faq.pregunta}</b> {faq.respuesta}</div>
                  ))}
                </div>
              )}
              <button className="btn btn-success" style={{marginTop:'10px'}} onClick={() => publicarBorrador(vistaPrevia.post_id)}>
                Publicar ahora
              </button>
            </div>
          )}

          <div className="card">
            <div className="card-title">Borradores guardados</div>
            {borradores.length === 0 && <div>No hay borradores.</div>}
            {borradores.map(b => (
              <div key={b.id} style={{marginBottom:'12px', borderBottom:'1px solid var(--border)', padding:'8px 0'}}>
                <div><strong>{b.titulo}</strong> (creado: {new Date(b.creado_en).toLocaleString()})</div>
                <div style={{marginTop:'6px'}}>
                  <button className="btn btn-success" style={{fontSize:'12px', marginRight:'8px'}} onClick={() => publicarBorrador(b.id)}>Publicar</button>
                  <button className="btn btn-danger" style={{fontSize:'12px'}} onClick={async () => {
                    await fetch(`${API_URL}/admin/blog/borradores/${b.id}`, { method: 'DELETE', headers: headers() });
                    cargarBorradores();
                  }}>Eliminar</button>
                </div>
              </div>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}